import useSafeState from "../../utils/safe-state";
import {Button, Col, Form, Input, InputNumber, Row, Select, Spin} from "antd";
import {useAxios} from "../../utils/hooks";
import {AxiosError, AxiosResponse} from "axios";
import React, {useEffect} from "react";
import {openNotification} from "../../utils/openNotification";
import {Trans} from "@lingui/macro";
import Dragger from "antd/es/upload/Dragger";
import {InboxOutlined} from "@ant-design/icons/lib";
import {ItemModel} from "../../models/items/item"
import fileDownload from "js-file-download";

interface Interface{
    data?: ItemModel;
    mode?: 'Edit' | 'Add new' | 'Upload File' | 'Copy';
}

const ItemDrawer = (props: Interface) => {

    const data = props.data || {};
    const [form] = Form.useForm();

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");

    const [fileList, setFileList] = useSafeState<Array<any>>([]);
    const [loading, setLoading] = useSafeState(false);
    const [suppliers, setSuppliers] = useSafeState<any[]>([]);
    const config = {headers: {'Content-Type': 'application/json'}};

    useEffect(() => {
        if (axiosInstance.current != null) {
            const payload = { 
                start: 0, 
                length: 1000,
                direction: 'ASC',
                sorted_field: 'id'
            };
            axiosInstance.current
                .post('/resource/suppliers/page', payload)
                .then((response: AxiosResponse<any>) => {
                    const supplierList = response.data.data || [];
                    setSuppliers(supplierList);
                })
                .catch((error: any) => {
                        openNotification('error', 'Failed to load suppliers');
                });
        }
    }, []);


    const uploadProps = {
        name: 'file',
        multiple: false,
        onChange(info: any) {
            const { status } = info.file;
            if (status === 'done') {
                openNotification('customSuccess', `${info.file.name} file uploaded successfully.`);
            } else if (status === 'error') {
                openNotification('customError', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            if (!isXlsx) {
                openNotification('customError', 'Only XLSX files are supported');
                return false;
            }
            let fileList2: Array<any> = fileList;
            fileList2.push(file);
            setFileList(fileList2);
            return false;
        },
    };

    const downloadTemplate = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/items/download-template', {
                    responseType: 'blob'
                })
                .then((response: AxiosResponse<any>) => {
                    fileDownload(response.data, 'items_template.xlsx');
                })
                .catch((error: any) => {
                    openNotification('error', 'Failed to download template');
                });
        }
    };

    const handleUploadExcel = () => {
        if (axiosInstance.current != null && fileList.length > 0) {
            setLoading(true);
            const formData = new FormData();
            fileList.forEach(file => {
                formData.append('files', file);
            });

            axiosInstance.current
                .post('/resource/items/upload-csv', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(() => {
                    openNotification('success', 'Items uploaded successfully');
                    setFileList([]);
                    setLoading(false);
                })
                .catch(function (error) {
                    const message = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Upload failed';
                    openNotification('error', message);
                    setLoading(false);
                });
        }
    };

    const handleEdit = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            const selectedSuppliers = form.getFieldValue(['suppliers']) || [];
            const supplierObjects = suppliers.filter(s => selectedSuppliers.includes(s.id));

            axiosInstance.current
                .put(
                    '/resource/items/id?fifoNumber=' + form.getFieldValue(['fifoNumber']),
                    {
                        fifoNumber: form.getFieldValue(["fifoNumber"]),
                        itemNo: form.getFieldValue(["itemNo"]),
                        quality: form.getFieldValue(['quality']),
                        sizeX: form.getFieldValue(['sizeX']),
                        sizeY: form.getFieldValue(['sizeY']),
                        normWeight: form.getFieldValue(['normWeight']),
                        suppliers: supplierObjects,
                        version: form.getFieldValue(['version'])
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Item updated successfully');
                    onReset();
                })
                .catch(function (error: any) {
                    const message = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Update failed';
                    openNotification('error', message);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        form.resetFields();

        if(props.mode === 'Copy' || props.mode === 'Edit') {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if (key === 'suppliers' && Array.isArray(value)) {
                    fieldsValues.push({ name: key, value: (value as any[]).map(s => s.id) });
                } else {
                    fieldsValues.push({ name: key, value: value });
                }
            }
            form.setFields(fieldsValues);
        }
    }, [props.mode, props.data]);

    const handleFinish = () => {
        if (axiosInstance.current != null) {
            setLoading(true);

            const selectedSuppliers = form.getFieldValue(['suppliers']) || [];
            const supplierObjects = suppliers.filter(s => selectedSuppliers.includes(s.id));

            const obj = {
                fifoNumber: form.getFieldValue(["fifoNumber"]),
                itemNo: form.getFieldValue(["itemNo"]),
                quality: form.getFieldValue(['quality']),
                sizeX: form.getFieldValue(['sizeX']),
                sizeY: form.getFieldValue(['sizeY']),
                normWeight: form.getFieldValue(['normWeight']),
                suppliers: supplierObjects
            };

            axiosInstance.current.post("/resource/items", obj, config)
            .then(function (response: AxiosResponse<any>) {
                setLoading(false);
                openNotification('success', 'Item created successfully');
                onReset();
            }).catch(function (error: AxiosError) {
                let errMessage = error && error.response && (error.response as any).data && (error.response as any).data.message ? (error.response as any).data.message : 'Error creating item';
                openNotification('error', errMessage);
                setLoading(false);
            });
        }
    }

    const onReset = () => {
        form.resetFields();
    };



    return (
        <Form
            id="newItem"
            layout='vertical'
            form={form}
            onFinish={() => (props.mode === 'Edit' ? handleEdit() : handleFinish())}
        >
            {props.mode === 'Edit' || props.mode === 'Add new' || props.mode === 'Copy' ? (
            <>
            <Spin spinning={loading}></Spin>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        name="itemNo"
                        label={<Trans>ItemNo</Trans>}
                        children={<Input disabled={props.mode === 'Edit'}/>}
                        rules={[{
                            type: "string",
                            required: true,
                            message: <Trans>Please enter the item no</Trans>
                        }]}
                    />
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="fifoNumber"
                        label={<Trans>FIFO</Trans>}
                        children={<InputNumber disabled={props.mode === 'Edit'}/>}
                        rules={[{
                            required: true,
                            message: <Trans>Please enter the FIFO number</Trans>
                        }]}
                    />
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={24}>
                    <Form.Item
                        name="suppliers"
                        label={<Trans>Suppliers</Trans>}
                        children={
                            <Select
                                mode="multiple"
                                placeholder="Select suppliers"
                                optionLabelProp="label"
                            >
                                {suppliers.map((supplier: any) => (
                                    <Select.Option key={supplier.id} value={supplier.id} label={supplier.name}>
                                        {supplier.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        }
                        rules={[{
                            required: true,
                            message: <Trans>Please select at least one supplier</Trans>
                        }]}
                    />
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        name="quality"
                        label={<Trans>Quality</Trans>}
                        children={<Input/>}
                        rules={[{
                            type: "string",
                            required: true,
                            message: <Trans>Please enter the quality</Trans>
                        }]}
                    />
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="normWeight"
                        label={<Trans>Norm Weight</Trans>}
                        children={<InputNumber/>}
                    />
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        name="sizeX"
                        label={<Trans>Size X</Trans>}
                        children={<InputNumber/>}
                        rules={[{
                            required: true,
                            message: <Trans>Please enter the size x</Trans>
                        }]}
                    />
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="sizeY"
                        label={<Trans>Size Y</Trans>}
                        children={<InputNumber/>}
                        rules={[{
                            required: true,
                            message: <Trans>Please enter the size y</Trans>
                        }]}
                    />
                </Col>
            </Row>
            </>
            ) : (
                <>
                    <Form.Item
                        name="file"
                        children={
                            <Dragger {...uploadProps}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">
                                    <Trans>Click or drag file to this area to upload</Trans>
                                </p>
                                <p className="ant-upload-hint">
                                    <Trans>Support for a single Excel file upload</Trans>
                                </p>
                            </Dragger>
                        }
                    />
                    <br />
                </>
            )}

            {props.mode === 'Edit' ? (
                <Button htmlType="button" type="primary" onClick={() => form.submit()}>
                    <Trans>Save</Trans>
                </Button>
            ) : props.mode === 'Add new' || props.mode === 'Copy' ? (
                <>
                    <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        loading={loading}
                        htmlType="button"
                        type="primary"
                        style={{ marginRight: 8 }}
                        onClick={() => form.submit()}
                    >
                        <Trans>Save</Trans>
                    </Button>
                </>
            ) : props.mode === 'Upload File' ? (
                <>
                    <Button style={{ marginRight: 8 }} onClick={() => downloadTemplate()}>
                        <Trans>Download Template</Trans>
                    </Button>
                    <Button loading={loading} htmlType="button" type="primary" onClick={() => handleUploadExcel()}>
                        <Trans>Upload</Trans>
                    </Button>
                </>
            ) : null}
        </Form>
    );

}
export default ItemDrawer;
